/**
MIT License

Copyright (c) 2017 Niladri

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
import { add, trim } from './add';
import { roundOff } from './round';
import { multiply } from './multiply';
import { divide } from './divide';
import { compareTo } from './compareTo';
import { subtract, negate } from './subtract';
import { RoundingModes as Modes } from './roundingModes';

export class BigDecimalService {
    private value: string;
    static RoundingModes = Modes;

    private static validate(number): string {
        if (number) {
            number = number.toString();
            if (isNaN(number))
                throw Error("Parameter is not a number: " + number);

            if (number[0] == '+')
                number = number.substring(1);
        }
        else
            number = '0';

        //handle exponentiation
        if (/e/i.test(number)) {
            let [mantisa, exponent] = number.split(/[eE]/);
            mantisa = trim(mantisa);
            exponent = parseInt(exponent) + mantisa.indexOf('.');
            mantisa = mantisa.replace('.', '');
            if (mantisa.length < exponent) {
                number = mantisa + (new Array(exponent - mantisa.length + 1)).join('0');
            }
            else if (mantisa.length >= exponent && exponent > 0) {
                number = trim(mantisa.substring(0, exponent)) +
                    ((mantisa.length > exponent) ? ('.' + mantisa.substring(exponent)) : '');
            }
            else {
                number = '0.' + (new Array(-exponent + 1)).join('0') + mantisa;
            }
        }

        return number;
    }

    constructor(number: number | string = '0') {
        this.value = BigDecimalService.validate(number);
    }

    getValue() {
        return this.value;
    }

    static getPrettyValue(number, digits, separator): string {
        if (!(digits || separator)) {
            digits = 3;
            separator = ',';
        }
        else if (!(digits && separator)) {
            throw Error('Illegal Arguments. Should pass both digits and separator or pass none');
        }
        number = BigDecimalService.validate(number);
        const neg = number.charAt(0) == '-';
        if (neg)
            number = number.substring(1);
        let len = number.indexOf('.');
        len = len > 0 ? len : (number.length);
        let temp = '';
        for (let i = len; i > 0;) {
            if (i < digits) {
                digits = i;
                i = 0;
            }
            else
                i -= digits;

            temp = number.substring(i, i + digits) + ((i < (len - digits) && i >= 0) ? separator : '') + temp;
        }
        return (neg ? '-' : '') + temp + number.substring(len);
    }
    getPrettyValue(digits, separator) {
        return BigDecimalService.getPrettyValue(this.value, digits, separator);
    }

    static round(number, precision = 0, mode = Modes.HALF_EVEN) {
        number = BigDecimalService.validate(number);
        if (isNaN(precision))
            throw Error("Precision is not a number: " + precision);
        return roundOff(number, precision, mode);
    }

    round(precision = 0, mode = Modes.HALF_EVEN) {
        if (isNaN(precision))
            throw Error("Precision is not a number: " + precision);

        return new BigDecimalService(roundOff(this.value, precision, mode));
    }

    static floor(number) {
        number = BigDecimalService.validate(number);
        if (number.indexOf('.') === -1)
            return number;
        return BigDecimalService.round(BigDecimalService.subtract(number, 0.5));
    }

    floor() {
        if (this.value.indexOf('.') === -1)
            return new BigDecimalService(this.value);
        return this.subtract(new BigDecimalService(0.5)).round();
    }

    static ceil(number) {
        number = BigDecimalService.validate(number);
        if (number.indexOf('.') === -1)
            return number;
        return BigDecimalService.round(BigDecimalService.add(number, 0.5));
    }

    ceil() {
        if (this.value.indexOf('.') === -1)
            return new BigDecimalService(this.value);
        return this.add(new BigDecimalService(0.5)).round();
    }


    static add(number1, number2) {
        number1 = BigDecimalService.validate(number1);
        number2 = BigDecimalService.validate(number2);
        return add(number1, number2);
    }

    add(number: BigDecimalService) {
        return new BigDecimalService(add(this.value, number.getValue()));
    }

    static subtract(number1, number2) {
        number1 = BigDecimalService.validate(number1);
        number2 = BigDecimalService.validate(number2);
        return subtract(number1, number2);
    }

    subtract(number: BigDecimalService) {
        return new BigDecimalService(subtract(this.value, number.getValue()));
    }

    static multiply(number1, number2) {
        number1 = BigDecimalService.validate(number1);
        number2 = BigDecimalService.validate(number2);
        return multiply(number1, number2);
    }

    multiply(number: BigDecimalService) {
        return new BigDecimalService(multiply(this.value, number.getValue()));
    }

    static divide(number1, number2, precision) {
        number1 = BigDecimalService.validate(number1);
        number2 = BigDecimalService.validate(number2);
        return divide(number1, number2, precision);
    }

    divide(number: BigDecimalService, precision) {
        return new BigDecimalService(divide(this.value, number.getValue(), precision));
    }

    static compareTo(number1, number2) {
        number1 = BigDecimalService.validate(number1);
        number2 = BigDecimalService.validate(number2);
        return compareTo(number1, number2);
    }

    compareTo(number: BigDecimalService) {
        return compareTo(this.value, number.getValue());
    }

    static negate(number) {
        number = BigDecimalService.validate(number);
        return negate(number);
    }

    negate() {
        return new BigDecimalService(negate(this.value));
    }
}
